import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import RepeatIcon from '@mui/icons-material/Repeat';
import { Avatar, Box, Tooltip, Typography } from '@mui/material';
import { alpha, darken, useTheme } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { EventProps } from 'react-big-calendar';
import type { DashboardSmallUser } from 'api/models/DashboardSmallUser';
import { ApiService } from 'api/services/ApiService';
import { getCalendarColorPalette } from 'dashboard409a/hooks/useColorPalette';
import { getInitials } from 'utilities/getInitials';
import {
  recurringIconSx,
  statusChipTextSx,
  taskEventRootSx,
  taskTitleSx,
  titleRowSx,
} from './WorkboardTaskEventComponent.styles';
import { WorkboardTaskCalendarEvent } from '../../ModelsCalendar.types';
import {
  chipsRowBoxSx,
  eventShellSx,
  labelChipOuterSx,
  labelChipTextSx,
  notesCountChipSx,
  notesCountIconSx,
  notesCountTextSx,
  notesTooltipAuthorTypographySx,
  notesTooltipBodyTypographySx,
  notesTooltipContentSx,
  peopleRowBoxSx,
  rushDealBadgeBoxSx,
  rushDealBadgeImageSx,
  statusBannerSx,
  waitingBadgeBoxSx,
} from '../EventComponent/EventComponent.styles';
import { buildLatestNotePreviewHtml } from '../EventComponent/EventComponent.utilities';
import { peopleGroupBoxSx, personAvatarDimensionsSx } from '../PeopleGroup/PeopleGroup.styles';

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const getUserLabel = (user: DashboardSmallUser) => {
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');
  return fullName || user.email || '';
};

const WorkboardTaskEventComponent = ({ event }: EventProps) => {
  const theme = useTheme();
  const taskEvent = event as WorkboardTaskCalendarEvent;
  const { resource } = taskEvent;
  const assigneeIds = resource.assignee_ids ?? [];
  const managerIds = resource.task_manager_ids ?? [];
  const hasAssignees = assigneeIds.length > 0 || managerIds.length > 0;

  const { data: staffUsers = [] } = useQuery<DashboardSmallUser[]>({
    queryKey: ['users-list', 'staff'],
    queryFn: async () => {
      const response = await ApiService.apiUsersStaffList();
      return response.filter(user => !user.email?.includes('+')) || [];
    },
    staleTime: 60 * 60 * 1000,
    enabled: hasAssignees,
  });

  const assignees = assigneeIds
    .map(userId => staffUsers.find(user => user.id === userId) ?? null)
    .filter((user): user is DashboardSmallUser => user != null);
  const managers = managerIds
    .map(userId => staffUsers.find(user => user.id === userId) ?? null)
    .filter((user): user is DashboardSmallUser => user != null);

  const statusLabel = STATUS_LABELS[resource.status] ?? resource.status;
  const isDark = theme.palette.mode === 'dark';

  const statusColors: Record<string, { bg: string; text: string }> = {
    pending: {
      bg: isDark ? alpha(theme.palette.warning.main, 0.35) : alpha(theme.palette.warning.light, 0.85),
      text: isDark ? theme.palette.warning.light : theme.palette.warning.dark,
    },
    in_progress: {
      bg: isDark ? alpha('#7c4dff', 0.35) : alpha('#ede7f6', 0.95),
      text: isDark ? '#b39ddb' : '#4527a0',
    },
    completed: {
      bg: isDark ? alpha(theme.palette.success.main, 0.35) : alpha(theme.palette.success.light, 0.85),
      text: isDark ? theme.palette.success.light : theme.palette.success.dark,
    },
    cancelled: {
      bg: isDark ? alpha(theme.palette.grey[500], 0.35) : alpha(theme.palette.grey[300], 0.85),
      text: isDark ? theme.palette.grey[400] : theme.palette.grey[700],
    },
  };

  const { bg: chipBg, text: chipText } = statusColors[resource.status] ?? {
    bg: isDark ? alpha('#00897b', 0.35) : alpha('#e0f2f1', 0.95),
    text: isDark ? '#80cbc4' : '#00695c',
  };

  const eventBg = isDark ? alpha('#00897b', 0.22) : alpha('#b2dfdb', 0.55);
  const eventBorder = '#00897b';
  const isRush = resource.is_rush ?? false;
  const rushHighlightColor = theme.palette.error.main;
  const titleColor = isDark ? theme.palette.common.white : '#004d40';
  const labelsBackgroundColor = getCalendarColorPalette().LABELS;
  const labelsTextColor = theme.palette.getContrastText(labelsBackgroundColor);
  const avatarBg = darken(labelsBackgroundColor, 0.2);
  const avatarInitialsColor = theme.palette.getContrastText(avatarBg);
  const displayDate = resource.display_date ? moment(resource.display_date).format('MMM, DD, YY') : '';
  const hasNotes = typeof resource.notes_count === 'number' && resource.notes_count > 0;
  const latestNote = resource.note;
  const latestNoteAuthor = latestNote?.author_name || latestNote?.author_email || '';
  const latestNoteCreatedAt = latestNote?.created_at
    ? moment(latestNote.created_at).format('MMM D, YYYY h:mm A')
    : '';
  const latestNotePreviewHtml = buildLatestNotePreviewHtml((latestNote?.content ?? '').trim());

  const renderPersonAvatar = (user: DashboardSmallUser, roleLabel: string) => (
    <Tooltip title={`${roleLabel}: ${getUserLabel(user)}`} placement="top" arrow>
      <Avatar
        key={user.id}
        src={user.picture_url ?? undefined}
        sx={{ ...personAvatarDimensionsSx, color: avatarInitialsColor }}
        style={{ backgroundColor: avatarBg }}>
        {getInitials({ name: `${user.first_name || ''} ${user.last_name || ''}` })}
      </Avatar>
    </Tooltip>
  );

  return (
    <Box sx={eventShellSx}>
      <Box sx={taskEventRootSx(eventBg, eventBorder, resource.is_waiting, isRush, rushHighlightColor)}>
        <Box sx={statusBannerSx(chipBg)}>
          <Typography variant="caption" sx={statusChipTextSx(chipText)}>
            {statusLabel}
          </Typography>
        </Box>
        <Box sx={titleRowSx}>
          {resource.is_recurring && <RepeatIcon sx={recurringIconSx(titleColor)} />}
          <Typography variant="caption" fontWeight="bold" sx={taskTitleSx(titleColor)}>
            {event.title}
          </Typography>
        </Box>
        {(displayDate || hasNotes) && (
          <Box sx={chipsRowBoxSx}>
            {displayDate && (
              <Box sx={labelChipOuterSx(labelsBackgroundColor)}>
                <Typography variant="caption" sx={labelChipTextSx(labelsTextColor)}>
                  {displayDate}
                </Typography>
              </Box>
            )}
            {hasNotes && (
              <Tooltip
                arrow
                placement="top"
                title={
                  <Box sx={notesTooltipContentSx}>
                    <Typography variant="caption" sx={notesTooltipAuthorTypographySx}>
                      {latestNoteAuthor || 'Analyst'} {latestNoteCreatedAt ? `• ${latestNoteCreatedAt}` : ''}
                    </Typography>
                    <Typography
                      variant="body2"
                      component="div"
                      sx={notesTooltipBodyTypographySx}
                      dangerouslySetInnerHTML={{ __html: latestNotePreviewHtml || '—' }}
                    />
                  </Box>
                }
              >
                <Box sx={notesCountChipSx(labelsBackgroundColor)}>
                  <ChatBubbleOutlineIcon sx={notesCountIconSx(labelsTextColor)} />
                  <Typography variant="caption" sx={notesCountTextSx(labelsTextColor)}>
                    {resource.notes_count}
                  </Typography>
                </Box>
              </Tooltip>
            )}
          </Box>
        )}
        <Box sx={peopleRowBoxSx}>
          <Box sx={peopleGroupBoxSx}>{assignees.map(user => renderPersonAvatar(user, 'Assigned'))}</Box>
          {isRush && (
            <Box sx={rushDealBadgeBoxSx}>
              <Box component="img" src="/images/rush-notice.png" alt="Rush" sx={rushDealBadgeImageSx} />
            </Box>
          )}
          <Box sx={peopleGroupBoxSx}>{managers.map(user => renderPersonAvatar(user, 'Manager'))}</Box>
        </Box>
      </Box>
      {resource.is_waiting && (
        <Box sx={waitingBadgeBoxSx(isDark ? alpha(theme.palette.warning.dark, 0.9) : theme.palette.warning.light)}>
          <Typography
            variant="caption"
            fontSize="0.7rem"
            sx={{ color: theme.palette.getContrastText(theme.palette.warning.light) }}>
            WAITING
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default WorkboardTaskEventComponent;
